/**
 * Model-stream SSE classifiers.
 *
 * ADR 0023 puts three kinds of thing on one connection. Conversation events
 * are the user's own and are classified by `./events.ts`. These are the other
 * kind: events on a MODEL's stream, routed to whoever is watching the model
 * rather than to a user, plus the three control frames that manage the watch
 * set (`connected`, `watching`, `reset`).
 *
 * Go-side definitions: `api-service-go/sse_handler.go` (streamEventBody,
 * resetBody), `api-service-go/event_subscriptions.go` (watchSetBody) and
 * `api-service-go/model_key.go` (the token grammar). The schemas live in this
 * file rather than in `./schemas.ts` because they are not response shapes of
 * any endpoint: they exist only on the wire, and keeping the classifier next
 * to the schema it enforces is what stops the two drifting.
 *
 * A stream event is recognised BY ITS SHAPE, not by its `event:` line. The
 * line carries the domain event type (`roster.published`), which
 * this client has no list of and must not need one of: a new event type on a
 * stream a board watches is data, not a client release.
 */

import { z } from "zod";

/**
 * A model key names one model resource: the subject it belongs to and the
 * stream on that subject. The same three parts appear in the URL that serves
 * the fold, in the watch token, and in every event published for it.
 */
export interface ModelKey {
  subjectType: string;
  subjectId: string;
  stream: string;
}

/** Render a key as the `watch` token the server parses. */
export function modelToken(model: ModelKey): string {
  return `${model.subjectType}:${model.subjectId}/${model.stream}`;
}

/**
 * Read a token back into a key. Returns null for anything it cannot fully
 * read, for the same reason the server refuses one: a half-read token is a
 * watch that matches nothing, and a watch that matches nothing is a board that
 * stays open and never updates.
 */
export function parseModelToken(token: string): ModelKey | null {
  const colon = token.indexOf(":");
  if (colon <= 0) return null;
  const slash = token.indexOf("/", colon + 1);
  if (slash <= colon + 1) return null;
  const stream = token.slice(slash + 1);
  if (stream === "") return null;
  return {
    subjectType: token.slice(0, colon),
    subjectId: token.slice(colon + 1, slash),
    stream,
  };
}

/** The body of a stream event: its model, its `seq`, and the payload. */
const StreamEventSchema = z
  .object({
    subject_type: z.string().min(1),
    subject_id: z.string().min(1),
    stream: z.string().min(1),
    seq: z.number().int(),
    event_type: z.string().min(1),
    payload: z.unknown(),
  })
  .strict();

/** The body of a `connected` or `watching` frame. */
const WatchSetSchema = z
  .object({
    connection_id: z.string().min(1),
    watching: z.array(z.string()),
  })
  .strict();

/** The body of a `reset` frame: the model whose watch the server tore down. */
const ResetSchema = z
  .object({
    subject_type: z.string().min(1),
    subject_id: z.string().min(1),
    stream: z.string().min(1),
  })
  .strict();

export interface StreamEvent {
  model: ModelKey;
  /**
   * The `sequence_number` of the appended event. It is the whole catch-up
   * mechanism: a client drops an event whose seq it has already applied, and
   * reads the model's `?after=<seq>` endpoint when it finds a gap. There is no
   * per-user replay for these — ADR 0023 keeps stream events out of the outbox
   * on purpose.
   */
  seq: number;
  eventType: string;
  payload: unknown;
}

export interface WatchSetFrame {
  connectionId: string;
  watching: string[];
}

/**
 * Classify a stream event by shape. `eventType` comes from the SSE envelope
 * and is checked against the copy in the body: the server writes it in both
 * places, so a mismatch means the frame was assembled by something that does
 * not agree with the contract and it is not safe to dispatch on either value.
 */
export function classifyStreamEvent(
  eventType: string,
  payload: unknown,
): { ok: true; event: StreamEvent } | { ok: false } {
  const parsed = StreamEventSchema.safeParse(payload);
  if (!parsed.success) return { ok: false };
  if (parsed.data.event_type !== eventType) return { ok: false };
  return {
    ok: true,
    event: {
      model: {
        subjectType: parsed.data.subject_type,
        subjectId: parsed.data.subject_id,
        stream: parsed.data.stream,
      },
      seq: parsed.data.seq,
      eventType: parsed.data.event_type,
      payload: parsed.data.payload,
    },
  };
}

/** Classify a `connected` or `watching` frame. */
export function classifyWatchSetFrame(
  payload: unknown,
): { ok: true; frame: WatchSetFrame } | { ok: false } {
  const parsed = WatchSetSchema.safeParse(payload);
  if (!parsed.success) return { ok: false };
  return {
    ok: true,
    frame: {
      connectionId: parsed.data.connection_id,
      watching: parsed.data.watching,
    },
  };
}

/** Classify a `reset` frame. */
export function classifyResetFrame(
  payload: unknown,
): { ok: true; model: ModelKey } | { ok: false } {
  const parsed = ResetSchema.safeParse(payload);
  if (!parsed.success) return { ok: false };
  return {
    ok: true,
    model: {
      subjectType: parsed.data.subject_type,
      subjectId: parsed.data.subject_id,
      stream: parsed.data.stream,
    },
  };
}
